import { Check, X } from 'lucide-react-native';
import { Pressable, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

export interface ApprovalCompany {
  readonly id: string;
  readonly name: string;
  readonly website: string | null;
  readonly instagramUrl: string | null;
  readonly linkedinUrl: string | null;
}

export function readApprovalCompanies(input: Record<string, unknown>): ApprovalCompany[] {
  if (!Array.isArray(input.companies)) return [];
  return input.companies.flatMap((row) => {
    const value = row as Record<string, unknown> | null;
    if (typeof value?.id !== 'string' || typeof value.name !== 'string') return [];
    const page = (key: string) => (typeof value[key] === 'string' ? (value[key] as string) : null);
    return [
      {
        id: value.id,
        name: value.name,
        website: page('website'),
        instagramUrl: page('instagramUrl'),
        linkedinUrl: page('linkedinUrl'),
      },
    ];
  });
}

function displayUrl(url: string): string {
  return url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

function publicPages(company: ApprovalCompany): string {
  const pages = [company.instagramUrl, company.linkedinUrl, company.website]
    .filter((url): url is string => Boolean(url))
    .map(displayUrl);
  return pages.length > 0 ? pages.join(' · ') : 'No public page found';
}

interface CompanyApprovalRowsProps {
  readonly companies: readonly ApprovalCompany[];
  readonly dropped: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly onKeep: (companyId: string) => void;
  readonly onDrop: (companyId: string) => void;
}

export function CompanyApprovalRows({
  companies,
  dropped,
  disabled,
  onKeep,
  onDrop,
}: CompanyApprovalRowsProps) {
  return (
    <Box style={styles.table}>
      <Box flexDirection="row" alignItems="center" style={styles.header}>
        <Text variant="label" color={colors.text.tertiary} style={styles.headerCompany}>
          Company
        </Text>
        <Text variant="label" color={colors.text.tertiary}>
          Keep
        </Text>
      </Box>
      {companies.map((company) => {
        const isDropped = dropped.has(company.id);
        return (
          <Box
            key={company.id}
            testID={`approval-company-row-${company.id}`}
            flexDirection="row"
            alignItems="center"
            gap="sm"
            style={[styles.row, isDropped && styles.rowDropped]}
          >
            <Box style={styles.rowText}>
              <Text
                variant="bodySmall"
                color={colors.text.primary}
                style={[styles.name, isDropped && styles.nameDropped]}
                numberOfLines={1}
              >
                {company.name}
              </Text>
              <Text variant="caption" color={colors.text.secondary} numberOfLines={2}>
                {publicPages(company)}
              </Text>
            </Box>
            <Pressable
              onPress={() => onKeep(company.id)}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={`Keep ${company.name}`}
              accessibilityState={{ selected: !isDropped, disabled }}
              hitSlop={6}
              style={[styles.toggle, !isDropped && styles.keepSelected]}
            >
              <Check
                size={16}
                color={isDropped ? colors.text.tertiary : colors.status.success.main}
                strokeWidth={2.5}
              />
            </Pressable>
            <Pressable
              onPress={() => onDrop(company.id)}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={`Drop ${company.name}`}
              accessibilityState={{ selected: isDropped, disabled }}
              hitSlop={6}
              style={[styles.toggle, isDropped && styles.dropSelected]}
            >
              <X
                size={16}
                color={isDropped ? colors.status.error.main : colors.text.tertiary}
                strokeWidth={2.5}
              />
            </Pressable>
          </Box>
        );
      })}
    </Box>
  );
}

const styles = StyleSheet.create({
  table: {
    marginLeft: 20,
    gap: tokens.spacing.xxs,
  },
  header: {
    paddingHorizontal: tokens.spacing.smPlus,
    paddingBottom: tokens.spacing.xxs,
  },
  headerCompany: {
    flex: 1,
  },
  row: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.smPlus,
    paddingHorizontal: tokens.spacing.smPlus,
    paddingVertical: tokens.spacing.sm,
  },
  rowDropped: {
    opacity: 0.6,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: '600',
  },
  nameDropped: {
    textDecorationLine: 'line-through',
  },
  toggle: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.full,
  },
  keepSelected: {
    backgroundColor: colors.status.success.bg,
  },
  dropSelected: {
    backgroundColor: colors.status.error.bg,
  },
});
